import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react"

import useSessionStorage from "hooks/useSessionStorage"
import { useLocation } from "store/contexts/location"
import { identifyUser } from "utils/identifyUser"
import { identifyEmailUser } from "utils/identifyEmailUser"
import { fetchSavedFolders, hasSavedItems } from "components/account/saved-items/savedFolders"

const UserContext = createContext(null)

const LOGGED_OUT_USER = {
  id: null,
  email: null,
  loggedIn: false,
  hasClassPaymentPlans: false,
  hasSavedArticles: false,
}

const fetchUser = async () => {
  const response = await fetch("/api/users/status")

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message)
  }

  return response.json()
}

export const UserProvider = ({ children }) => {
  const { location } = useLocation()
  const [user, setUser] = useSessionStorage("user", LOGGED_OUT_USER)
  const [checkedForUser, setCheckedForUser] = useSessionStorage("checkedForUser", false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const getUser = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      const data = await fetchUser()
      setUser({
        id: data.userId,
        email: data.user.email,
        loggedIn: true,
        hasClassPaymentPlans: data.hasClassPaymentPlans,
        hasSavedArticles: data.hasSavedArticles,
      })
    } catch (err) {
      setUser(LOGGED_OUT_USER)
      setError(err.message)
    }

    setCheckedForUser(true)
    setLoading(false)
  }, [setUser, setCheckedForUser])

  const clearUser = useCallback(() => {
    setUser(LOGGED_OUT_USER)
    setCheckedForUser(false)
  }, [setUser, setCheckedForUser])

  const setHasSavedArticles = useCallback(
    (hasSavedArticles) =>
      // same object when unchanged, so consumers and the identify effect don't re-run
      setUser((prev) =>
        prev.hasSavedArticles === hasSavedArticles ? prev : { ...prev, hasSavedArticles },
      ),
    [setUser],
  )

  // null = the status check failed, undefined = cached before the flag existed;
  // re-check both, otherwise the flag reads as false
  useEffect(() => {
    if (loading || !user.loggedIn || user.hasSavedArticles != null) return undefined
    let cancelled = false
    fetchSavedFolders()
      .then((folders) => {
        if (!cancelled) setHasSavedArticles(hasSavedItems(folders))
      })
      .catch((err) => console.error("Could not check saved articles:", err))
    return () => {
      cancelled = true
    }
  }, [loading, user.loggedIn, user.hasSavedArticles, setHasSavedArticles])

  useEffect(() => {
    if (!checkedForUser) {
      if (!["/account/callback/login", "/account/callback/logout"].includes(location.pathname)) {
        getUser()
      } else {
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [location, getUser, checkedForUser])

  useEffect(() => {
    if (user.email) {
      identifyUser({ user })
    }
  }, [user])

  useEffect(() => {
    // if there is an mbg_hash in the url, identify the user
    const params = new URLSearchParams(location.search)
    const hash = params.get("mbg_hash")
    if (hash) {
      identifyUser({ hash })
    }
  }, [location.search])

  useEffect(() => {
    // if there is an mbg_pid (Klaviyo Profile ID) in the url, resolve identity via Segment/Klaviyo
    const params = new URLSearchParams(location.search)
    const mbgPid = params.get("mbg_pid")
    if (mbgPid) {
      identifyEmailUser(location)
    }
  }, [location.search])

  useEffect(() => {
    // push logged in data layer for Permutive
    window.pDataLayer = window.pDataLayer || {}
    window.pDataLayer.user = { loggedIn: user.loggedIn }
  }, [user.loggedIn])

  return (
    <UserContext.Provider
      value={useMemo(
        () => ({
          user,
          getUser,
          clearUser,
          setHasSavedArticles,
          loading,
          error,
        }),
        [user, getUser, clearUser, setHasSavedArticles, loading, error],
      )}
    >
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error(`useUser must be used within a UserProvider`)
  }
  return context
}
